import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import claudeLogo from "../../../public/img/claude-color.svg";

const CHARS_PER_SECOND = 360;

function useTypewriter(fullText, enabled) {
  const [shown, setShown] = useState(enabled ? "" : fullText);
  const animatedKeyRef = useRef(null);
  const rafRef = useRef(null);
  // A live preview grows fullText one fragment at a time (and the buffered
  // message then extends it); keep revealing from where we are instead of
  // restarting from "" each time.
  const shownLenRef = useRef(enabled ? 0 : fullText.length);
  const prevTextRef = useRef(fullText);

  useEffect(() => {
    const key = `${enabled ? "live" : "static"}::${fullText.length}::${fullText.slice(0, 24)}`;
    if (!enabled) {
      setShown(fullText);
      shownLenRef.current = fullText.length;
      animatedKeyRef.current = key;
      return undefined;
    }
    if (animatedKeyRef.current === key) return undefined;
    animatedKeyRef.current = key;
    const from = fullText.startsWith(prevTextRef.current) ? Math.min(shownLenRef.current, fullText.length) : 0;
    prevTextRef.current = fullText;
    setShown(fullText.slice(0, from));
    const total = fullText.length;
    if (total <= from) return undefined;
    let startedAt = null;
    let lastShownLength = from;
    const tick = (now) => {
      if (startedAt == null) startedAt = now;
      const elapsedSec = (now - startedAt) / 1000;
      const target = Math.min(total, from + Math.floor(elapsedSec * CHARS_PER_SECOND));
      if (target !== lastShownLength) {
        lastShownLength = target;
        shownLenRef.current = target;
        setShown(fullText.slice(0, target));
      }
      if (target < total) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        rafRef.current = null;
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [fullText, enabled]);

  return shown;
}

/**
 * Agent message — Claude logo avatar on the left, prose on the right. The
 * prose still renders as raw markdown (no bubble); the avatar gives the
 * message a clear "from Claude" identity without re-introducing the chat-app
 * bubble feel.
 */
function AgentMessageItem({ event, showAvatar = true }) {
  const text = event.payload?.text || "";
  const live = !!event._live;
  const shown = useTypewriter(text, live);
  // A preview keeps its cursor between fragments so it doesn't blink on/off.
  const stillStreaming = live && (shown.length < text.length || !!event._preview);

  return (
    <div className="ca-msg ca-msg--agent">
      <div className="ca-msg-avatar" aria-hidden="true">
        {showAvatar && <img src={claudeLogo} alt="" />}
      </div>
      <div className="ca-msg-prose">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            // Wide tables scroll inside their own box instead of widening the chat.
            table({ node, ...rest }) {
              return <div className="ca-md-table-wrap"><table {...rest} /></div>;
            },
            code({ inline, className, children, ...rest }) {
              const lang = /language-(\w+)/.exec(className || "")?.[1];
              if (inline || !lang) {
                return <code className={className} {...rest}>{children}</code>;
              }
              return (
                <SyntaxHighlighter language={lang} PreTag="div">
                  {String(children).replace(/\n$/, "")}
                </SyntaxHighlighter>
              );
            },
          }}
        >
          {shown}
        </ReactMarkdown>
        {stillStreaming && <span className="ca-msg-cursor" aria-hidden="true" />}
      </div>
    </div>
  );
}

// Memoized: old agent messages must not re-render (re-running markdown + Prism)
// every time a new event streams in. `event` refs are stable per message.
export default React.memo(AgentMessageItem);
